"use client";

import React, { useState } from "react";
import { CircleCheck, Clock, MapPin, Calendar, ArrowRight, ArrowLeft, ShieldCheck, CloudUpload, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useFormContext } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { cn } from "@/lib/utils";
const checklistItems = [{
  icon: <Clock className="size-5 text-primary" />,
  title: "24-Hour Cash Offer",
  description: "Submit your details and receive a fair, no-obligation cash offer within one business day."
}, {
  icon: <ShieldCheck className="size-5 text-primary" />,
  title: "Zero Repair Costs",
  description: "We buy your property exactly as it is. No cleaning, no repairs, and no renovations required."
}, {
  icon: <CircleCheck className="size-5 text-primary" />,
  title: "No Hidden Fees",
  description: "We cover all closing costs. The offer we make is the amount you receive at the closing table."
}];
function AssessmentFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const {
    trigger
  } = useFormContext();
  const [step, setStep] = useState(1);
  const totalSteps = 2;
  const progress = step / totalSteps * 100;
  const nextStep = async () => {
    const fieldsToValidate = step === 1 ? ["full_name", "email", "phone"] : ["property_address", "preferred_date", "additional_details"];
    const isValid = await trigger(fieldsToValidate);
    if (isValid) {
      setStep(prev => Math.min(prev + 1, totalSteps));
    }
  };
  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };
  if (isSubmitted && !submitError) {
    return <div data-section-id="32772" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
                <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <CircleCheck className="size-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-foreground tracking-tighter">Offer Request Sent!</h3>
                <p className="text-muted-foreground max-w-xs mx-auto">
                    {successMessage}
                </p>
                <Button variant="outline" className="mt-6 border-primary text-primary hover:bg-primary/5 rounded-sm" onClick={() => window.location.reload()}>
                    Submit Another Property
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
            <div className="mb-6 space-y-2">
                <div className="flex justify-between text-xs font-mono font-medium text-muted-foreground uppercase tracking-wider">
                    <span>Step {step} of {totalSteps}</span>
                    <span>{step === 1 ? "Owner Details" : "Property Info"}</span>
                </div>
                <Progress value={progress} className="h-1.5 bg-accent" />
            </div>

            <div className={cn("space-y-4", step === 1 ? "block" : "hidden")}>
                <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-semibold">Full Name</FormLabel>
                            <FormControl>
                                <Input placeholder="John Smith" className="rounded-sm border-input focus:ring-primary" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-semibold">Email Address</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="john@example.com" className="rounded-sm border-input focus:ring-primary" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-semibold">Phone Number</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="(555) 000-0000" className="rounded-sm border-input focus:ring-primary" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <div className={cn("space-y-4", step === 2 ? "block" : "hidden")}>
                <FormField control={control} name="property_address" rules={{
        required: "Property Address is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-semibold">Property Address</FormLabel>
                            <FormControl>
                                <Input placeholder="123 Street Name, City, State" className="rounded-sm border-input focus:ring-primary" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-semibold">Desired Closing Date</FormLabel>
                            <FormControl>
                                <Input type="date" className="rounded-sm border-input focus:ring-primary" {...field} />
                            </FormControl>
                            <FormDescription className="text-xs">
                                We can close in as little as 7 days or on your timeline.
                            </FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="additional_details" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-semibold">Property Condition & Notes</FormLabel>
                            <FormControl>
                                <textarea className="flex min-h-[100px] w-full rounded-sm border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50" placeholder="Tell us about any major repairs needed or your reason for selling..." {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <div className="rounded-sm border border-dashed border-border bg-accent/30 p-6 text-center transition-colors hover:bg-accent/50">
                    <div className="mx-auto flex size-10 items-center justify-center rounded-full bg-background shadow-sm mb-3">
                        <CloudUpload className="size-5 text-muted-foreground" />
                    </div>
                    <p className="text-sm font-semibold text-foreground">Have Photos?</p>
                    <p className="text-xs text-muted-foreground mt-1">
                        Sharing photos of the property helps us provide a more accurate cash offer faster.
                    </p>
                </div>
            </div>

            {submitError && <div className="p-3 rounded-sm bg-destructive/10 text-destructive text-sm font-medium">
                    {submitError}
                </div>}

            <div className="flex gap-3 pt-2">
                {step > 1 && <Button type="button" variant="outline" onClick={prevStep} disabled={isSubmitting} className="w-full rounded-sm border-input font-bold">
                        <ArrowLeft className="mr-2 size-4" /> BACK
                    </Button>}
                
                {step < totalSteps ? <Button type="button" onClick={nextStep} className="w-full rounded-sm bg-primary text-primary-foreground font-bold hover:bg-primary/90">
                        NEXT STEP <ArrowRight className="ml-2 size-4" />
                    </Button> : <Button type="submit" disabled={isSubmitting} className="w-full rounded-sm bg-primary text-primary-foreground font-bold hover:bg-primary/90 tracking-wider">
                        {isSubmitting ? <>
                                <Loader className="mr-2 size-4 animate-spin" />
                                PROCESSING...
                            </> : "GET MY OFFER NOW"}
                    </Button>}
            </div>
        </Form>;
}
export default function ClaimYourAssessment() {
  return <section id="get-your-free-cash-offer" className="bg-muted text-foreground relative overflow-hidden py-20 lg:py-28">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent pointer-events-none" />
            <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579273/img/noise.png')] opacity-[0.02] mix-blend-overlay pointer-events-none" />

            <div className="container relative mx-auto px-4 md:px-6">
                <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-start">
                    
                    <div className="flex flex-col justify-center space-y-8 lg:sticky lg:top-24">
                        <div className="space-y-4">
                            <h2 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl text-foreground uppercase">
                                Sell Your House <span className="text-primary">Fast For Cash</span>
                            </h2>
                            <p className="text-xl text-muted-foreground max-w-[600px] leading-relaxed">
                                Skip the repairs, the agents, and the months of waiting. We buy properties in any condition across the region with a transparent, no-fee process.
                            </p>
                        </div>

                        <div className="space-y-6">
                            <h3 className="text-xs font-mono font-bold text-primary uppercase tracking-widest">The DHTproperties Promise</h3>
                            <div className="grid gap-6">
                                {checklistItems.map((item, i) => <div key={i} data-index={i} className="flex gap-4 items-start">
                                        <div className="mt-1 flex size-10 shrink-0 items-center justify-center rounded-sm bg-background border border-border shadow-sm">
                                            {item.icon}
                                        </div>
                                        <div className="space-y-1">
                                            <h4 className="font-bold leading-none text-foreground uppercase tracking-tight">
                                                {item.title}
                                            </h4>
                                            <p className="text-sm text-muted-foreground">
                                                {item.description}
                                            </p>
                                        </div>
                                    </div>)}
                            </div>
                        </div>

                        <div className="rounded-sm bg-background border border-border p-6 shadow-sm">
                            <div className="flex items-center gap-4">
                                
                                <div className="text-sm">
                                    <p className="font-bold text-foreground">Trusted by 500+ Homeowners</p>
                                    <p className="text-muted-foreground font-medium">Local investors you can count on</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="relative">
                        <div className="absolute -inset-1 rounded-sm bg-primary/10 blur-2xl opacity-50" />
                        <Card className="relative border-border bg-background shadow-2xl rounded-sm">
                            <CardHeader className="space-y-1 pb-6 border-b border-border/50">
                                <CardTitle className="text-2xl font-bold tracking-tighter uppercase">Get Your Free Cash Offer</CardTitle>
                                <CardDescription className="text-base">
                                    Complete the form below to receive your no-obligation offer within 24 hours.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="pt-8">
                                <FormProvider formId="get_cash_offer_form" sectionName="get-cash-offer-section" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                property_address: "",
                preferred_date: "",
                additional_details: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true
                },
                property_address: {
                  required: true
                }
              }} submitText="GET MY OFFER NOW" successMessage="Thank you! Your property information has been received. Our team will review the details and contact you with a fair cash offer within 24 hours.">
                                    <AssessmentFormContent />
                                </FormProvider>
                            </CardContent>
                            <CardFooter className="justify-center border-t border-border/50 bg-muted/30 py-4">
                                <p className="text-xs font-mono font-medium text-muted-foreground text-center flex items-center gap-1.5 uppercase">
                                    <ShieldCheck className="size-3.5 text-primary" />
                                    Privacy Guaranteed • No Hidden Fees
                                </p>
                            </CardFooter>
                        </Card>
                    </div>
                </div>
            </div>
        </section>;
}